/**
 * Money on the billing screens. Every amount the billing API sends is an
 * integer number of cents (USD); the sign follows the ledger: positive is
 * what the organisation owes FinderlyFix, negative a credit or a payment.
 */

const USD = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** The typographic minus, so "−$45.00" reads as one negative amount (and a screen reader says "minus"). */
export const MINUS = '−';

/** `4500` → "$45.00", `-4500` → "−$45.00", `123456` → "$1,234.56". */
export function formatCents(cents: number | null | undefined): string {
  const n = typeof cents === 'number' && Number.isFinite(cents) ? Math.round(cents) : 0;
  const abs = USD.format(Math.abs(n) / 100);
  return n < 0 ? `${MINUS}$${abs}` : `$${abs}`;
}

/** Basis points as a percentage: `3000` → "30%", `2550` → "25.5%". */
export function formatBps(bps: number | null | undefined): string {
  const n = typeof bps === 'number' && Number.isFinite(bps) ? bps : 0;
  const pct = n / 100;
  return `${Number.isInteger(pct) ? pct : Number(pct.toFixed(2))}%`;
}

/**
 * A dollar amount typed by a person ("45", "45.5", "$1,200.00", "-12.30")
 * as cents, or null when it is not a number. At most two decimal places.
 */
export function parseDollars(input: string): number | null {
  const s = input.trim().replace(/[$,\s]/g, '').replace(MINUS, '-');
  if (!/^-?\d+(\.\d{1,2})?$/.test(s) && !/^-?\.\d{1,2}$/.test(s)) return null;
  const cents = Math.round(Number(s) * 100);
  return Number.isFinite(cents) ? cents : null;
}

/** Cents as the plain dollar text an input holds: `4500` → "45.00". */
export function centsToInput(cents: number | null | undefined): string {
  if (typeof cents !== 'number' || !Number.isFinite(cents)) return '';
  return (cents / 100).toFixed(2);
}
